import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { DesignAvatar } from '@components/design-avatar';
import { EmptyState } from '@components/empty-state';
import { useDeleteModal } from '@components/generic-modals';
import { Header } from '@components/header';
import { IconAI } from '@components/icons/ai';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorInfoCurrentColor } from '@components/icons/form-indicator-info-current-color';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import { ListCard } from '@components/list-card';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { Separator } from '@components/separator';
import { useSidebarState } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useAIAgents, useRemoveAIAgent } from 'api/ai-agents/hooks';
import { AIAgent } from 'api/ai-agents/requests';
import { useUserOption } from 'api/authentication/hooks';
import { AIModel, AIProvider } from 'api/types';

import { useAgentDetailsSidebar } from './agent-details-sidebar';
import { useDuplicateAIAgentTemplateModal } from './duplicate-modal';

const MODEL_PROVIDER_NAMES: Record<AIModel, AIProvider> = {
  'gpt-4o': 'OpenAI',
  'gpt-4o-mini': 'OpenAI',
  o1: 'OpenAI',
  o3: 'OpenAI',
  'o3-mini': 'OpenAI',
  'o4-mini': 'OpenAI',
  'gpt-4.1': 'OpenAI',
  'gpt-4.1-mini': 'OpenAI',
  'gpt-4.1-nano': 'OpenAI',
  'gpt-5': 'OpenAI',
  'gpt-5-mini': 'OpenAI',
  'gpt-5-nano': 'OpenAI',
};

export const getAgentIcon = (aiAgent: AIAgent, iconSize: number = 40) => {
  return aiAgent.picture ? (
    <DesignAvatar src={aiAgent.picture} size={iconSize} />
  ) : (
    <IconAI size={iconSize} />
  );
};

export const AIAgentList = () => {
  const { data: aiAgents, isLoading: isAIAgentsLoading } = useAIAgents();
  const { t } = useScopedTranslate(locale);
  const isDataEmpty = !aiAgents || aiAgents.length === 0;
  const { value: hasHiddenInstructions, update } = useUserOption(
    'hasHiddenAiAgentInstructions',
    false
  );
  const showAgentDetailsSidebar = useAgentDetailsSidebar();
  const { activeId } = useSidebarState();
  const showDeleteModal = useDeleteModal();
  const showDuplicateModal = useDuplicateAIAgentTemplateModal();
  const removeAIAgentMutation = useRemoveAIAgent();

  if (isAIAgentsLoading) return <Spinner />;

  return (
    <>
      <Header
        actions={
          <Stack orientation="horizontal" space={4}>
            <Button
              data-cy="new-journey-btn"
              text={t('createAgent')}
              iconLeft={<IconAiAgent />}
              onClick={() => showAgentDetailsSidebar()}
            />
          </Stack>
        }
        breadcrumbs={<Breadcrumb items={[{ label: t('aiAgents') }]} />}
      />
      <PageContent>
        {isDataEmpty ? (
          <EmptyState
            skipMargin
            header={t('emptyStateHeader')}
            content={t('emptyStateContent')}
            icon={<IconAI size={80} />}
          />
        ) : (
          <Stack space={6}>
            {!hasHiddenInstructions && (
              <InfoBox
                font="regular14"
                text={t('instructions')}
                variant="indigo"
                icon={<IconFormIndicatorInfoCurrentColor />}
                closable
                onClose={() => update.mutate(true)}
              />
            )}
            <JourneyItemContainer space={2}>
              {aiAgents?.map((aiAgent) => {
                return (
                  <ListCard
                    key={aiAgent.shortId}
                    title={aiAgent.name}
                    description={aiAgent.description}
                    isActive={activeId === aiAgent.shortId}
                    icon={getAgentIcon(aiAgent)}
                    onClick={() => showAgentDetailsSidebar(aiAgent, { id: aiAgent.shortId })}
                    actions={
                      <Stack orientation="horizontal" space={4} align="center">
                        <Tag size="large" text="Active" variant="green" />
                        <Separator orientation="horizontal" />
                        <Stack align="center" style={{ minWidth: pxToRem(80) }}>
                          <Text
                            font="bold14"
                            text={MODEL_PROVIDER_NAMES[aiAgent.model]}
                            color="textPrimary"
                          />
                          <Text font="regular12" text={aiAgent.model} color="textSecondary" />
                        </Stack>
                        <Separator orientation="horizontal" />
                        <Menu buttonProps={{ variant: 'ghost', iconRight: <IconMoreVertical /> }}>
                          <Menu.Item
                            label={t('core.edit')}
                            icon={<IconEdit />}
                            onSelect={() =>
                              showAgentDetailsSidebar(aiAgent, { id: aiAgent.shortId })
                            }
                          />
                          <Menu.Item
                            label={t('core.duplicate')}
                            icon={<IconDuplicate />}
                            onSelect={() => showDuplicateModal({ aiAgent })}
                          />
                          <Menu.Item
                            label={t('core.delete')}
                            icon={<IconTrash />}
                            variant="danger"
                            onSelect={() =>
                              showDeleteModal({
                                title: t('deleteTitle'),
                                description: styledTranslate({
                                  key: 'deleteDescription',
                                  variables: {
                                    note: {
                                      text: t('deleteNote'),
                                      font: 'bold16',
                                    },
                                  },
                                  translations: locale,
                                }),
                                hasUserConfirmation: true,
                                buttonText: t('core.delete'),
                                buttonIcon: <IconTrash />,
                                onConfirm: () => removeAIAgentMutation.mutate(aiAgent.shortId),
                              })
                            }
                          />
                        </Menu>
                      </Stack>
                    }
                  />
                );
              })}
            </JourneyItemContainer>
          </Stack>
        )}
      </PageContent>
    </>
  );
};

const JourneyItemContainer = styled(Stack)({
  flexGrow: 1,
  position: 'relative',
  display: 'flex',
  '@media(max-width: 768px)': {
    overflowY: 'visible',
  },
});

const locale = {
  en: {
    aiAgents: 'AI Agents',
    createAgent: 'Create Agent',
    emptyStateContent:
      'Create and manage AI agents for your company. These agents can be used to empower and enhance your journey workflows',
    emptyStateHeader: 'You do not have any AI Agents',
    instructions:
      'Create and manage AI agents for your company. These agents can be used to empower your journey workflows',
    deleteTitle: 'Delete AI Agent',
    deleteDescription:
      'You are about to permanently delete this AI Agent template. Once deleted, it will no longer be available for selection in the journey builder for creating new agent actions.\n\n{note}: Existing agent actions created from this template will continue to function normally.',
    deleteNote: 'Note',
  },
};
